import { Button } from "@/components/bnp-ui";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import StepOne from "./StepOne";
import StepTwo from "./StepTwo";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { LiveInspectionService, TransactionsService } from "@/services";
import useAuthToken from "@/hooks/useAuthToken";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useCompatRouter as useRouter } from "@/lib/compat-router/context";
import parseHttpError from "@/utils/parseHttpError";

const Bundles = () => {
  const { user } = useAuthToken();
  const router = useRouter();
  const [step, setStep] = useState(1);
  const [selectedSource, setSelectedSource] = useState("Wallet");
  const [walletDetails, setWalletDetails] = useState<any>();
  const [selectedButton, setSelectedButton] = useState("5");
  const [amountPayable, setAmountPayable] = useState("");
  const [isChecked, setIsChecked] = useState(false);
  const { trxref, amountPaid, amountToBuy } = router.query;

  const defaultValues = {
    amount: "",
  };

  const validationSchema = yup.object().shape({
    amount: yup.string(),
  });

  const {
    getValues,
    setValue,
    control,
    handleSubmit,
    setError,
    watch,
    trigger,
    formState: { errors, isValid },
  } = useForm({
    defaultValues,
    resolver: yupResolver(validationSchema),
    mode: "onChange",
  });
  const handleNext = async () => {
    if (selectedButton === ">20") {
      if (!getValues().amount) {
        toast.error("Please enter a custom amount greater than 20");
      }
    } else if (!isChecked) {
      toast.error("Please tick checkbox to proceed");
    } else {
      setStep(step + 1);
    }
  };

  const getWalletDetailsRequest = async () => {
    try {
      const response = await TransactionsService.GetWallet();
      // console.log(response?.data);
      return response?.data;
    } catch (error: any) {
      console.log(error);
      throw new Error(parseHttpError(error).message);
    }
  };

  const {
    data: walletData,
    isLoading: walletDataIsLoading,
    isError: walletDataIsError,
    refetch: walletDataRefetch,
    isSuccess: walletDataIsSuccess,
  } = useQuery({
    queryKey: ["walletDetails"],
    queryFn: getWalletDetailsRequest,
    enabled: !!user,
  });

  useEffect(() => {
    if (walletData) {
      setWalletDetails(walletData?.data);
    }
  }, [walletData]);

  const paystackRequest = async () => {
    try {
      const response = await TransactionsService.InitiateTransaction({
        customerEmail: user?.email,
        amount: Number(amountPayable),
        callback_url: `https://findnigeriaproperties.com/user/dashboard/properties/my-subscriptions?amountToBuy=${selectedButton}&amountPaid=${amountPayable}`,
      });
      return response?.data;
    } catch (error: any) {
      console.log(error);
      throw new Error(parseHttpError(error).message);
    }
  };

  const purchaseBundleViaWalletRequest = async () => {
    try {
      const debitResponse = await TransactionsService.DebitFNP({
        amount: amountPayable,
        reason: "Bundle Purchase",
      });

      if (!debitResponse?.data) {
        throw new Error("Transaction failed, no transaction ID returned.");
      }

      const txnId = debitResponse.data.transactionId;

      const response = await LiveInspectionService.PurchaseBundleViaWallet({
        amount_per_inspections: 30000,
        amount_to_buy: Number(selectedButton),
        total_payable_amount: Number(amountPayable),
        transaction_id: txnId,
      });

      return response?.data;
    } catch (error: any) {
      // console.error(error);
      throw new Error(parseHttpError(error).message);
    }
  };

  const walletMutation: any = useMutation({
    mutationFn: purchaseBundleViaWalletRequest,
    onSuccess: (res: any) => {
      // router.push(`${res?.data?.authorization_url}`);
      // close();
      toast.success("Bundle purchased successfully!");
      router.reload();
    },
    onError: (error: any) => {
      toast.error(error.message || "Request failed");
    },
  });

  const paystackMutation: any = useMutation({
    mutationFn: paystackRequest,
    onSuccess: (res: any) => {
      router.push(`${res?.data?.authorization_url}`);
      close();
    },
    onError: (error: any) => {
      toast.error(error.message || "Request failed");
    },
  });

  const onSubmit = () => {
    if (selectedSource === "Paystack") {
      paystackMutation.mutate();
    } else {
      walletMutation.mutate();
    }
  };

  const balance = walletData?.data?.wallet?.available_balance ?? 0;
  return (
    <div className="w-[100%]">
      <div className="p-3 flex flex-col gap-2 w-[100%] h-fit border border-bnp-cyan bg-bnp-teal rounded-lg">
        <h2 className="text-main-900 font-semibold text-sm">
          What is a bundle?
        </h2>
        <p className="text-sm font-normal text-grey-7">
          A bundle is a subscription for a minimum number of live inspection
          services starting from five (5) live inspection services. Choose
          higher bundles to enjoy great discounts!
        </p>
      </div>
      <div className="w-[100%] border-b border-soft-200 my-4 py-4">
        {step === 1 && (
          <StepOne
            control={control}
            setValue={setValue}
            selectedButton={selectedButton}
            setSelectedButton={setSelectedButton}
            amountPayable={amountPayable}
            setAmountPayable={setAmountPayable}
            isChecked={isChecked}
            setIsChecked={setIsChecked}
          />
        )}

        {step === 2 && (
          <StepTwo
            amountPayable={amountPayable}
            selectedSource={selectedSource}
            setSelectedSource={setSelectedSource}
            walletDetails={walletDetails}
          />
        )}
      </div>

      <div className="flex w-[100%] justify-between">
        <div className="flex items-center gap-[10px]">
          <div
            className={`w-2 h-2 rounded-full transition-all ${
              step === 1 ? "bg-bnp-cyan" : "bg-soft-200"
            }`}
          ></div>
          <div
            className={`w-2 h-2 rounded-full transition-all ${
              step === 2 ? "bg-bnp-cyan" : "bg-soft-200"
            }`}
          ></div>
        </div>
        <div className="flex gap-3 items-center mb-8">
          {step > 1 && (
            <Button
              className="bg-white border border-border-1 text-bnp-dark text-sm font-medium w-[94px] h-[60px] rounded-lg"
              onClick={() => {
                setStep(step - 1);
              }}
            >
              Back
            </Button>
          )}

          {step !== 2 && (
            <Button
              className="bg-bnp-dark text-white text-sm font-medium w-[94px] h-[60px] rounded-lg"
              onClick={handleNext}
              disabled={false}
            >
              Next
            </Button>
          )}

          {step === 2 && (
            <Button
              className="bg-bnp-dark text-white text-sm font-medium w-[194px] h-[60px] rounded-lg"
              onClick={onSubmit}
              isLoading={paystackMutation.isPending || walletMutation.isPending}
              disabled={Number(balance) < Number(amountPayable)}
            >
              {selectedSource === "Wallet"
                ? "Pay via wallet"
                : "Pay via Paystack"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Bundles;
